/**
 * REAL: Keep Horizon's recently viewed history behind Shopify preference consent.
 */
export class RecentlyViewed {
  static eventName = 'real:recently-viewed-change';

  /** @static @constant {string} The key used to store viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** The latest explicit preference choice, without browsing history or identifiers. */
  static #PREFERENCE_KEY = 'real:preferencesConsent';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;
  static #initialized = false;
  static #loading = false;
  static #revision = 0;
  /** @type {boolean | null} */
  static #allowed = null;
  /** Only the current page's product is retained while consent loads. @type {string | null} */
  static #currentProductId = null;

  static get revision() {
    return this.#revision;
  }

  static initialize() {
    if (this.#initialized) return;
    this.#initialized = true;

    document.addEventListener('visitorConsentCollected', this.#syncConsent);
    window.addEventListener('load', this.#loadConsentAPI, { once: true });
    window.addEventListener('pageshow', this.#syncConsent);
    window.addEventListener('storage', (event) => {
      if (event.key !== this.#STORAGE_KEY && event.key !== this.#PREFERENCE_KEY && event.key !== null) return;
      // Shopify can cache a different consent decision in another tab. A
      // history notification must not let that stale decision erase a new visit.
      // Reads remain permission-gated, and clears still invalidate visible rows.
      this.#notify();
    });
    this.#loadConsentAPI();
  }

  static #loadConsentAPI = () => {
    if (window.Shopify?.customerPrivacy?.preferencesProcessingAllowed) {
      this.#syncConsent();
      return;
    }
    if (this.#loading) return;
    if (!window.Shopify?.loadFeatures) {
      if (document.readyState === 'complete') this.#syncConsent();
      return;
    }

    this.#loading = true;
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        this.#loading = false;
        this.#syncConsent();
      });
    } catch {
      this.#loading = false;
      this.#syncConsent();
    }
  };

  /** Use Shopify's region-aware decision; an unavailable API never grants access. */
  static isAllowed() {
    return this.#shopifyAllowsPreferences() && this.#getPreferenceSignal() !== 'denied';
  }

  static #shopifyAllowsPreferences() {
    try {
      return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;
    } catch {
      return false;
    }
  }

  static #getPreferenceSignal() {
    try {
      return localStorage.getItem(this.#PREFERENCE_KEY);
    } catch {
      return 'denied';
    }
  }

  /** @param {Event} [event] */
  static #syncConsent = (event) => {
    const preferenceChoice = event?.type === 'visitorConsentCollected'
      ? /** @type {CustomEvent} */ (event).detail?.preferencesAllowed
      : undefined;
    const explicitChoice = typeof preferenceChoice === 'boolean';
    if (explicitChoice) {
      // A negative gate protects other tabs whose Shopify API still caches an
      // earlier grant. Only an explicit choice can lift it, with Shopify's approval.
      const signal = preferenceChoice && this.#shopifyAllowsPreferences() ? 'allowed' : 'denied';
      try {
        localStorage.setItem(this.#PREFERENCE_KEY, signal);
      } catch {
        // The local decision and its history cleanup remain effective.
      }
    }
    // pageshow can precede the async API callback. Keep existing history unread
    // until Shopify resolves the decision instead of treating startup as denial.
    if (
      !explicitChoice && this.#loading && this.#allowed === null &&
      !window.Shopify?.customerPrivacy?.preferencesProcessingAllowed
    ) return;
    const allowed = this.isAllowed();
    const changed = allowed !== this.#allowed;
    this.#allowed = allowed;

    if (!allowed) {
      // Passive cached denials must not erase a newer explicit grant elsewhere.
      // A real withdrawal always clears, including a repeated explicit denial.
      if (explicitChoice || (this.#getPreferenceSignal() !== 'allowed' && (changed || event?.type !== 'pageshow'))) {
        this.#removeStoredProducts();
      }
    } else if ((changed || explicitChoice) && this.#currentProductId) {
      this.#storeProduct(this.#currentProductId);
    }
    if (changed || explicitChoice) this.#notify();
  };

  static #notify() {
    this.#revision += 1;
    document.dispatchEvent(new CustomEvent(this.eventName));
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (typeof productId !== 'string' || !/^\d+$/.test(productId)) return;
    this.#currentProductId = productId;
    if (!this.isAllowed()) return;
    this.#storeProduct(productId);
    this.#notify();
  }

  /** @param {string} productId */
  static #storeProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Storage can be unavailable; ordinary search must keep working.
    }
  }

  static clearProducts() {
    this.#currentProductId = null;
    this.#removeStoredProducts();
    this.#notify();
  }

  static #removeStoredProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Denied storage access must not interrupt consent changes or search.
    }
  }

  /**
   * Retrieves recently viewed products only while preference processing is allowed.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (!this.isAllowed()) return [];
    try {
      const products = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (!Array.isArray(products)) throw new Error('Invalid recently viewed history');
      return [...new Set(products.filter((id) => typeof id === 'string' && /^\d+$/.test(id)))].slice(
        0,
        this.#MAX_PRODUCTS
      );
    } catch {
      this.#removeStoredProducts();
      return [];
    }
  }
}

RecentlyViewed.initialize();
